/**
 * IMGLIB-1 · ④ 从图片库选（弹窗）。契约：图片库-样稿.html 第 4 页。
 * 默认先列这条线行程里那几个景点的图；可切「某个地点全部」「全部地点」+ 搜名字；已经在这条线里的标「已在」；类型不对的灰掉不让选。
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { KIND_LABEL, listLibrary, listPlaces, MEDIA_QK, type LibraryFilters, type MediaKind } from "@/services/mediaLibrary";
import { MediaTile } from "./MediaTile";

type Scope = "trip" | "place" | "all";

export function MediaPickModal({ open, onClose, onConfirm, slot, productLabel, existingIds, tripAttractionIds }: {
  open: boolean;
  onClose: () => void;
  onConfirm: (mediaIds: string[]) => void;
  slot: MediaKind;
  productLabel: string;
  existingIds: ReadonlySet<string>;
  /** 这条线行程里出现过的景点 id（去重） */
  tripAttractionIds: string[];
}) {
  const [scope, setScope] = useState<Scope>(tripAttractionIds.length ? "trip" : "all");
  const [placeId, setPlaceId] = useState("");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  useEffect(() => { if (open) { setPicked([]); setQ(""); setScope(tripAttractionIds.length ? "trip" : "all"); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const placesQ = useQuery({ queryKey: MEDIA_QK.places, queryFn: listPlaces, enabled: open });
  const filters: LibraryFilters = useMemo(() => ({
    ...(scope === "trip" ? { attractions: tripAttractionIds.join(",") } : {}),
    ...(scope === "place" && placeId ? { place: placeId } : {}),
    ...(q.trim() ? { q: q.trim() } : {}),
    limit: 500,
  }), [scope, placeId, q, tripAttractionIds]);
  const listQ = useQuery({
    queryKey: MEDIA_QK.library(filters),
    enabled: open && (scope !== "trip" || tripAttractionIds.length > 0),
    queryFn: () => listLibrary(filters),
  });
  const items = scope === "trip" && tripAttractionIds.length === 0 ? [] : (listQ.data?.items ?? []);

  if (!open) return null;
  const toggle = (id: string) => setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const chip = (on: boolean) => `rounded-full border px-3 py-1 text-[13px] ${on ? "border-blue-600 bg-blue-600 font-semibold text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm animate-in fade-in" role="dialog" aria-label="从图片库选"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col gap-3 overflow-hidden rounded-2xl bg-white p-6 shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4" data-testid="media-pick-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">从图片库选 · {productLabel} · {slot === "gallery" ? "产品相册" : "宣传长图"}</h3>
          <button type="button" aria-label="关闭" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={chip(scope === "trip")} onClick={() => setScope("trip")}>本线行程里的景点（{tripAttractionIds.length}）</button>
          <button type="button" className={chip(scope === "place")} onClick={() => setScope("place")}>按地点</button>
          {scope === "place" && (
            <select aria-label="地点" value={placeId} onChange={(e) => setPlaceId(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1 text-sm">
              <option value="">全部地点</option>
              {(placesQ.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name_cn}</option>)}
            </select>
          )}
          <button type="button" className={chip(scope === "all")} onClick={() => setScope("all")}>全部地点</button>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 搜名字" aria-label="搜名字"
            className="ml-auto min-w-[160px] flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="grid min-h-[200px] flex-1 auto-rows-max grid-cols-2 content-start items-start gap-3 overflow-y-auto sm:grid-cols-3 md:grid-cols-4" data-testid="media-pick-grid">
          {listQ.isLoading && <p className="col-span-full py-8 text-center text-sm text-slate-400">加载中…</p>}
          {!listQ.isLoading && items.length === 0 && <p className="col-span-full py-8 text-center text-sm text-slate-400">没有符合条件的图片</p>}
          {items.map((a) => {
            const already = existingIds.has(a.id);
            const wrongKind = a.kind !== slot;
            return (
              <MediaTile key={a.id} asset={a} data-testid="media-pick-tile" badge={already ? "已在" : wrongKind ? `${a.kind === "highlight" ? "竖版" : "横版"} · ${slot === "gallery" ? "相册" : "宣传图"}用不了` : undefined}
                selected={picked.includes(a.id)} dim={already || wrongKind} onClick={() => toggle(a.id)} />
            );
          })}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[12px] text-slate-500">默认先列这条线行程里那 {tripAttractionIds.length} 个景点的图；已经在这条线里的标「已在」；类型不对的（{KIND_LABEL[slot === "gallery" ? "highlight" : "gallery"]}）灰掉不让选。</span>
          <span className="ml-auto flex gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">取消</button>
            <button type="button" data-testid="media-pick-confirm" disabled={picked.length === 0} onClick={() => { onConfirm(picked); onClose(); }}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">加入 {picked.length} 张</button>
          </span>
        </div>
      </div>
    </div>
  );
}
